import type { VaultGraph, VaultLink, VaultNode } from '../../shared/types';

export interface PositionedNode extends VaultNode {
  position: [number, number, number];
  radius: number;
}

export interface PositionedGraph {
  nodes: PositionedNode[];
  links: VaultLink[];
  nodeById: Map<string, PositionedNode>;
}

export function layoutGraph(
  graph: VaultGraph,
  focusNodeIds = new Set<string>(),
  primaryFocusNodeId?: string
): PositionedGraph {
  const areas = graph.areas.filter((area) => area !== 'Vault' && area !== 'Unresolved');
  const areaCenters = new Map<string, [number, number, number]>();
  const shellRadius = Math.max(30, areas.length * 3.7);
  const verticalSpread = Math.max(16, areas.length * 1.8);

  areas.forEach((area, index) => {
    const angle = index * 2.399963229728653 + hashUnit(area) * 0.9;
    const tier = ((index % 5) - 2) / 2;
    const depth = (hashUnit(`${area}:depth`) - 0.5) * shellRadius * 0.45;
    areaCenters.set(area, [
      Math.cos(angle) * shellRadius * (0.68 + hashUnit(`${area}:x`) * 0.28),
      tier * verticalSpread + (hashUnit(`${area}:y`) - 0.5) * 10,
      Math.sin(angle) * shellRadius * (0.68 + hashUnit(`${area}:z`) * 0.28) + depth
    ]);
  });

  const nodes = graph.nodes.map<PositionedNode>((node, index) => {
    const radius = nodeRadius(node);
    const position = nodePosition(
      node,
      index,
      areaCenters,
      graph.nodes.length,
      focusNodeIds,
      primaryFocusNodeId
    );
    return { ...node, position, radius };
  });
  settleLayout(nodes, graph.links, areaCenters, primaryFocusNodeId, focusNodeIds);

  const nodeById = new Map(nodes.map((node) => [node.id, node]));

  return {
    nodes,
    links: graph.links,
    nodeById
  };
}

function settleLayout(
  nodes: PositionedNode[],
  links: VaultLink[],
  areaCenters: Map<string, [number, number, number]>,
  primaryFocusNodeId: string | undefined,
  focusNodeIds: Set<string>
): void {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const linkSample = links.filter((link) => {
    if (link.kind === 'contains') return false;
    if (!nodeById.has(link.source) || !nodeById.has(link.target)) return false;
    if (link.source === primaryFocusNodeId || link.target === primaryFocusNodeId) return true;
    if (focusNodeIds.has(link.source) || focusNodeIds.has(link.target)) return true;
    const threshold = link.kind === 'property' ? 0.42 : link.kind === 'wikilink' ? 0.3 : 0.22;
    return stableHashUnit(`settle:${link.id}`) < threshold;
  });

  const maxIterations = nodes.length > 900 ? 18 : 28;
  const scratch = new Map<string, [number, number, number]>();

  for (let iteration = 0; iteration < maxIterations; iteration += 1) {
    scratch.clear();

    for (const link of linkSample) {
      const source = nodeById.get(link.source);
      const target = nodeById.get(link.target);
      if (!source || !target || isPinned(source, primaryFocusNodeId, focusNodeIds) || isPinned(target, primaryFocusNodeId, focusNodeIds)) {
        continue;
      }

      const sx = source.position[0];
      const sy = source.position[1];
      const sz = source.position[2];
      const dx = target.position[0] - sx;
      const dy = target.position[1] - sy;
      const dz = target.position[2] - sz;
      const distance = Math.max(0.001, Math.hypot(dx, dy, dz));
      const desired = link.kind === 'property' ? 13 : link.kind === 'wikilink' ? 16 : 19;
      const force = Math.max(-0.28, Math.min(0.28, (distance - desired) * 0.0045));
      const fx = (dx / distance) * force;
      const fy = (dy / distance) * force * 0.72;
      const fz = (dz / distance) * force;

      addDelta(scratch, source.id, fx, fy, fz);
      addDelta(scratch, target.id, -fx, -fy, -fz);
    }

    for (const node of nodes) {
      if (isPinned(node, primaryFocusNodeId, focusNodeIds)) continue;
      const center = areaCenters.get(node.area);
      if (!center || node.kind === 'unresolved') continue;
      const pull = node.kind === 'folder' ? 0.018 : 0.006;
      addDelta(
        scratch,
        node.id,
        (center[0] - node.position[0]) * pull,
        (center[1] + node.depth * 1.8 - node.position[1]) * pull * 0.6,
        (center[2] - node.position[2]) * pull
      );
    }

    for (const node of nodes) {
      const delta = scratch.get(node.id);
      if (!delta) continue;
      node.position = [
        node.position[0] + delta[0],
        node.position[1] + delta[1],
        node.position[2] + delta[2]
      ];
    }
  }
}

function addDelta(deltas: Map<string, [number, number, number]>, id: string, x: number, y: number, z: number): void {
  const current = deltas.get(id);
  if (current) {
    current[0] += x;
    current[1] += y;
    current[2] += z;
  } else {
    deltas.set(id, [x, y, z]);
  }
}

function isPinned(node: PositionedNode, primaryFocusNodeId: string | undefined, focusNodeIds: Set<string>): boolean {
  return node.id === 'folder:.' || node.id === primaryFocusNodeId || focusNodeIds.has(node.id);
}

function nodePosition(
  node: VaultNode,
  index: number,
  areaCenters: Map<string, [number, number, number]>,
  total: number,
  focusNodeIds: Set<string>,
  primaryFocusNodeId?: string
): [number, number, number] {
  if (node.id === 'folder:.') return [0, 0, 0];

  if (primaryFocusNodeId === node.id) {
    return [0, 22, 42];
  }

  if (focusNodeIds.has(node.id)) {
    const orbit = stableHashUnit(`${node.id}:focus`) * Math.PI * 2;
    const lane = (index % 7) - 3;
    const radius = 7 + stableHashUnit(`${node.id}:focus-radius`) * 16;
    return [
      Math.cos(orbit) * radius,
      18 + lane * 3.2 + stableHashUnit(`${node.id}:focus-y`) * 5,
      28 + Math.sin(orbit) * radius * 0.65
    ];
  }

  if (node.kind === 'unresolved') {
    const angle = hashUnit(node.id) * Math.PI * 2;
    const radius = 56 + hashUnit(`${node.id}:r`) * 20;
    return [
      Math.cos(angle) * radius,
      -22 + hashUnit(`${node.id}:y`) * 44,
      Math.sin(angle) * radius
    ];
  }

  const center = areaCenters.get(node.area) ?? [0, 0, 0];
  const areaSeed = hashUnit(node.area);
  const local = hashUnit(node.id);
  const angle = (local * Math.PI * 2 + areaSeed * Math.PI) % (Math.PI * 2);
  const verticalPhase = Math.sin(local * Math.PI * 6 + node.depth);
  const depthLift = Math.min(18, node.depth * 2.35);
  const spread = node.kind === 'folder' ? 7 + node.depth * 2.9 : 11 + Math.log(total) * 2.05;
  const radial = spread * Math.sqrt(hashUnit(`${node.id}:spread`));
  const zDrift = (hashUnit(`${node.id}:drift`) - 0.5) * spread * 1.25;
  const y =
    node.kind === 'folder'
      ? center[1] + depthLift - 7
      : center[1] + depthLift + verticalPhase * 12 + (hashUnit(`${node.id}:y`) - 0.5) * 22;

  return [
    center[0] + Math.cos(angle) * radial,
    y,
    center[2] + Math.sin(angle) * radial + zDrift
  ];
}

function nodeRadius(node: VaultNode): number {
  if (node.kind === 'folder') return node.depth === 0 ? 1.55 : 0.88;
  if (node.kind === 'unresolved') return 0.26;
  const degree = node.incoming + node.outgoing;
  return 0.24 + Math.min(0.62, Math.sqrt(degree) * 0.075);
}

export function stableHashUnit(value: string): number {
  return hashUnit(value);
}

function hashUnit(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 4294967295;
}
